import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { filter, map, withLatestFrom } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Card } from "src/model/class/general classes/card";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { ManufacturerSystem } from "src/components/shared-components/enum/enumMisc";
import {
  CardProgrammer,
  CardProgrammerReadRequest,
  CardProgrammerReadResponse,
  CardProgrammerWriteRequest,
} from "src/model/interface/card-programmer";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { cardProgrammerStartLoadAction } from "src/state/actions/card-programmer.actions";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { EventService } from "./event.service";

@Injectable({
  providedIn: "root",
})
export class CardProgrammerService implements iEvent {
  public needToInitializa: boolean = true;

  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _eventService: EventService
  ) {
    this.subscribeEvents();
  }

  /** Unsubscribe from observables */
  ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.Init();
  }

  /**
   *  Initialize public observable list trasmission:
   *  it should be call in the costructor of all component/page that you need.
   */
  public Init(): void {
    this._store.dispatch(cardProgrammerStartLoadAction());

    if (this.needToInitializa) {
      this.needToInitializa = false;
    }
  }

  /**
   * Get full list of card programmer
   *
   * @returns
   */
  public getListCardProgrammer(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_CARD_PROGRAMMER
    );
  }

  /**
   * Handle generic request read card
   *
   * @param cardProgrammerToUse Card programmer to use for reading
   * @returns
   */
  public readCard(
    cardProgrammerToUse: CardProgrammer
  ): Promise<CardProgrammerReadResponse> {
    const readRequest: CardProgrammerReadRequest = {
      cardProgrammer: cardProgrammerToUse,
    };
    return this._http
      .post<CardProgrammerReadResponse>(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD_PROGRAMMER +
        "/read",
        readRequest
      )
      .toPromise();
  }

  /**
   * Handle generic card write request
   *
   * @param idCard id card to write
   * @param cardProgrammerToUse card programmer to use for writing
   */
  public writeCardProgrammer(
    idCard: number,
    cardProgrammerToUse: CardProgrammer
  ): Promise<Card> {
    const writeRequest: CardProgrammerWriteRequest = {
      IdCardToWrite: idCard,
      cardProgrammer: cardProgrammerToUse,
    };

    return this._http
      .post<Card>(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD_PROGRAMMER +
        "/write/cardprogrammer",
        writeRequest
      )
      .toPromise();
  }

  /**
   * Set UID on specific card
   * @param idCard
   * @param UID
   * @returns
   */
  public async writeMobile(
    idCard: number,
    UID: string,
    manufacturerSystem: ManufacturerSystem
  ) {
    const bodyRequest = {
      uid: UID.toUpperCase(),
      manufacturer: manufacturerSystem,
    };
    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD_PROGRAMMER +
        "/" +
        idCard +
        "/write/mobile",
        bodyRequest
      )
      .pipe()
      .toPromise();
  }

  /**
   * Check if current manufacturer system neet to be write by
   * device card programmer
   *
   * @param manufacturerSystem Manufacturer system to check
   * @returns
   */
  public cardNeedToBeWrite(manufacturerSystem: ManufacturerSystem): boolean {
    let returnValue = false;

    switch (manufacturerSystem) {
      case ManufacturerSystem.VIMAR_EELECTRON:
      case ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS:
      case ManufacturerSystem.SCHNEIDER:
      case ManufacturerSystem.SIEMENS:
      case ManufacturerSystem.EELECTRON:
      case ManufacturerSystem.GEWISS:
      case ManufacturerSystem.ABB_MAC2:
      case ManufacturerSystem.ELECTRONE:
      case ManufacturerSystem.DORMAKABASAFLOKSYSTEM6000:
      case ManufacturerSystem.ABB:
      case ManufacturerSystem.SALTO_IS:
      case ManufacturerSystem.ABB_MILLENIUM:
      case ManufacturerSystem.ABB_TACTEO:
      case ManufacturerSystem.DAHUA_CARD_READER:
      case ManufacturerSystem.EELECTRON_MIFARE:
      case ManufacturerSystem.BIG_VIRTUAL_MIFARE:
        returnValue = true;
        break;
    }

    return returnValue;
  }

  //#region Event subscribtion

  /** Subscribe to EventService observables */
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this.reloadServiceData();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this.needToInitializa = true;
      })
    );
  }

  /** Unsubscribe from all EventService observables */
  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) sub.unsubscribe();
  }
  //#endregion
}
